import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { VehicleMatch } from '@/types';
import {
  formatPercentage,
  formatManualConsumptionSummary,
  formatPbevVehicleLabel,
  formatPbevVehicleSpecs,
} from '@/utils/helpers';
import { Check, Edit, Trash2, X } from 'lucide-react';

interface MatchTableProps {
  matches: VehicleMatch[];
  onVerify: (matchId: number, isVerified: boolean) => void;
  onDelete: (matchId: number) => void;
  onEditManualConsumption: (match: VehicleMatch) => void;
}

export function MatchTable({
  matches,
  onVerify,
  onDelete,
  onEditManualConsumption,
}: MatchTableProps) {
  const getConfidenceBadge = (score: number) => {
    if (score >= 0.85) return <Badge variant="success">High</Badge>;
    if (score >= 0.70) return <Badge variant="warning">Medium</Badge>;
    return <Badge variant="destructive">Low</Badge>;
  };

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>FIPE Vehicle</TableHead>
            <TableHead>PBEV Vehicle</TableHead>
            <TableHead>Confidence</TableHead>
            <TableHead>Tier</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {matches.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} className="text-center text-muted-foreground">
                No matches match the current filters.
              </TableCell>
            </TableRow>
          ) : (
            matches.map((match) => {
              const pbevSpecs = formatPbevVehicleSpecs(match);

              return (
                <TableRow key={match.id}>
                  <TableCell>
                    <div>
                      <div className="font-medium text-sm">{match.fipe_vehicle}</div>
                      <div className="text-xs text-muted-foreground">
                        Model ID: {match.model_id}
                        {match.fipe_id && ` (FIPE ID: ${match.fipe_id})`}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div className="font-medium text-sm">{formatPbevVehicleLabel(match)}</div>
                      {pbevSpecs && (
                        <div className="text-xs text-muted-foreground">{pbevSpecs}</div>
                      )}
                      <div className="text-xs text-muted-foreground">
                        PBEV ID: {match.pbev_id}
                      </div>
                      {match.manual_consumption && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          Approved override: {formatManualConsumptionSummary(match.manual_consumption)}
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getConfidenceBadge(match.confidence_score)}
                      <span className="text-sm">{formatPercentage(match.confidence_score)}</span>
                    </div>
                    {match.llm_confidence != null && (
                      <div
                        className="mt-1 flex items-center gap-1"
                        title={match.llm_reason ?? undefined}
                      >
                        <Badge variant="secondary">LLM</Badge>
                        <span className="text-xs text-muted-foreground">
                          {formatPercentage(match.llm_confidence)}
                        </span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">Tier {match.match_tier}</Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      {match.is_verified && <Badge variant="success">Verified</Badge>}
                      {match.is_manual && <Badge variant="secondary">Manual match</Badge>}
                      {match.algorithm_used === 'llm_rematch' && (
                        <Badge variant="outline" title={match.llm_reason ?? undefined}>LLM match</Badge>
                      )}
                      {match.audit_status === 'rejected' && (
                        <Badge variant="destructive" title="Rejeitado pela auditoria LLM">Audit rejected</Badge>
                      )}
                      {match.manual_consumption && <Badge variant="outline">Manual consumption</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2 justify-end">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEditManualConsumption(match)}
                      >
                        <Edit className="h-4 w-4" />
                        Edit consumption
                      </Button>
                      <Button
                        size="sm"
                        variant={match.is_verified ? 'outline' : 'default'}
                        onClick={() => onVerify(match.id, !match.is_verified)}
                      >
                        {match.is_verified ? (
                          <>
                            <X className="h-4 w-4 mr-1" />
                            Unverify
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Verify
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => onDelete(match.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
